// What one unit in the file means in millimetres.
//
// The viewer works in millimetres: STEP and IGES carry their unit and
// OpenCascade converts for us, so those always arrive correct. Mesh formats do
// not all agree. glTF's spec fixes the unit at metres; 3MF defaults to
// millimetres; STL, OBJ and PLY carry no unit at all. Getting this wrong does
// not distort the model — every measurement is simply off by a constant factor.

export type ModelUnit = 'mm' | 'cm' | 'm' | 'in';

export const MODEL_UNITS: ModelUnit[] = ['mm', 'cm', 'm', 'in'];

const MM_PER_UNIT: Record<ModelUnit, number> = {
  mm: 1,
  cm: 10,
  m: 1000,
  in: 25.4
};

export const MODEL_UNIT_LABEL: Record<ModelUnit, string> = {
  mm: 'Millimetres',
  cm: 'Centimetres',
  m: 'Metres',
  in: 'Inches'
};

export function mmPerUnit(unit: ModelUnit): number {
  return MM_PER_UNIT[unit];
}

/** The unit the model on screen is being read in. */
let currentUnit: ModelUnit = 'mm';

export function getModelUnit(): ModelUnit {
  return currentUnit;
}

export function setCurrentModelUnit(unit: ModelUnit): void {
  currentUnit = unit;
}

/**
 * The unit a file of this kind is written in, going by its own format spec.
 * glTF 2.0 states metres outright; the unitless formats get millimetres, which
 * is what CAD exports use in practice.
 */
export function defaultUnitFor(fileName: string): ModelUnit {
  return /\.(glb|gltf)$/i.test(fileName) ? 'm' : 'mm';
}

/** Formats that carry no unit, so the viewer is guessing and should say so. */
export function unitIsAssumed(fileName: string): boolean {
  return /\.(stl|obj|ply|3mf)$/i.test(fileName);
}

/**
 * A unit that would make an implausible model plausible, or null when the
 * current one is fine. Nothing people open here is smaller than a grain of
 * rice or larger than a city block, so a model outside that range is almost
 * always a unit mix-up rather than a genuinely odd part.
 */
export function suggestUnit(longestSideMm: number, current: ModelUnit): ModelUnit | null {
  if (longestSideMm <= 0) return null;
  const PLAUSIBLE_MIN = 5;          // 5 mm
  const PLAUSIBLE_MAX = 300_000;    // 300 m
  if (longestSideMm >= PLAUSIBLE_MIN && longestSideMm <= PLAUSIBLE_MAX) return null;

  const perUnit = MM_PER_UNIT[current];
  let best: ModelUnit | null = null;
  let bestDistance = Infinity;
  for (const candidate of MODEL_UNITS) {
    if (candidate === current) continue;
    const sideMm = (longestSideMm / perUnit) * MM_PER_UNIT[candidate];
    if (sideMm < PLAUSIBLE_MIN || sideMm > PLAUSIBLE_MAX) continue;
    // Prefer the candidate that lands nearest a hand-sized object (~300mm),
    // on a log scale so metres and millimetres are judged even-handedly.
    const distance = Math.abs(Math.log(sideMm / 300));
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  return best;
}
